import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getColleges, createCollege, deleteCollege } from '../services/api';
import { FiPlus, FiTrash2, FiGlobe, FiAlertCircle, FiHome, FiArrowRight } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import EmptyState from '../components/EmptyState';
import ConfirmDialog from '../components/ConfirmDialog';

const Colleges = ({ onLogout }) => {
  const navigate = useNavigate();
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;
  const isAdmin = user?.role === 'admin';

  const [colleges, setColleges] = useState([]);
  const [formData, setFormData] = useState({
    collegeName: '',
    collegeCode: '',
    domain: ''
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [deletingCollege, setDeletingCollege] = useState(null);

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const data = await getColleges();
      setColleges(data || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch colleges list');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.collegeName || !formData.collegeCode || !formData.domain) {
      setError('Please fill in all fields (College Name, Code, and Email Domain)');
      return;
    }

    try {
      setActionLoading(true);
      await createCollege({
        collegeName: formData.collegeName.trim(),
        collegeCode: formData.collegeCode.trim(),
        domain: formData.domain.trim().toLowerCase().replace(/^@/, '')
      });
      setSuccess('College created successfully!');
      setFormData({ collegeName: '', collegeCode: '', domain: '' });
      fetchColleges();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add college');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingCollege) return;
    try {
      setError(null);
      setSuccess(null);
      await deleteCollege(deletingCollege._id);
      setSuccess(`College "${deletingCollege.collegeName}" deleted successfully`);
      setDeletingCollege(null);
      fetchColleges();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete college');
    }
  };

  if (!isAdmin) {
    return (
      <div className="dashboard-container">
        <Sidebar onLogout={onLogout} />
        <div className="main-content">
          <div className="alert alert-error">
            <FiAlertCircle size={24} />
            <span>Access Denied: Only administrators can access and manage colleges.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar onLogout={onLogout} />
      <div className="main-content">
        <Header 
          title="Colleges Directory" 
          subtitle="View and manage institution details, faculty, students, and registration email domains."
        />

        {error && <div className="alert alert-error mb-4">{error}</div>}
        {success && <div className="alert alert-success mb-4">{success}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 1.5fr' : '1fr', gap: '24px' }}>
          {/* Add College Form */}
          {isAdmin && (
            <div className="card">
              <h3 className="mb-3" style={{ fontSize: '16px', fontWeight: '600' }}>
                <FiPlus style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Add New College
              </h3>
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>College Name *</label>
                  <input
                    type="text"
                    name="collegeName"
                    value={formData.collegeName}
                    onChange={handleChange}
                    placeholder="e.g. IIIT Surat"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>College Code *</label>
                  <input
                    type="text"
                    name="collegeCode"
                    value={formData.collegeCode}
                    onChange={handleChange}
                    placeholder="e.g. IIITS"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Allowed Email Domain *</label>
                  <input
                    type="text"
                    name="domain"
                    value={formData.domain}
                    onChange={handleChange}
                    placeholder="e.g. iiitsurat.ac.in"
                    className="form-input"
                    required
                  />
                  <span className="text-secondary text-sm mt-1" style={{ display: 'block' }}>
                    Only emails ending with <code>@{formData.domain || 'domain.ac.in'}</code> can register for this college.
                  </span>
                </div>

                <button type="submit" className="btn btn-primary w-100 mt-4" disabled={actionLoading}>
                  <FiPlus /> {actionLoading ? 'Adding...' : 'Add College'}
                </button>
              </form>
            </div>
          )}

          {/* Registered Colleges Grid */}
          <div className="card">
            <h3 className="mb-4" style={{ fontSize: '18px', fontWeight: '600' }}>
              Registered Institutions ({colleges.length})
            </h3>

            {loading ? (
              <div className="text-center p-4">
                <div className="spinner mx-auto"></div>
                <p className="mt-2 text-secondary">Loading colleges...</p>
              </div>
            ) : colleges.length === 0 ? (
              <EmptyState
                icon={<FiHome />}
                title="No colleges registered"
                description="Add your first institution to enable student and teacher registrations."
              />
            ) : (
              <div className="college-grid">
                {colleges.map((college) => (
                  <div
                    key={college._id}
                    className="college-card"
                    onClick={() => navigate(`/colleges/${college._id}`)}
                  >
                    <div>
                      <div className="college-card-header">
                        <div className="college-icon-box">
                          <FiHome />
                        </div>
                        <div className="college-title-group">
                          <span className="college-code-badge">
                            CODE: {college.collegeCode}
                          </span>
                          <h4 className="mt-1">{college.collegeName}</h4>
                        </div>
                      </div>

                      <div className="college-domain-info">
                        <FiGlobe style={{ color: 'var(--primary)', flexShrink: 0 }} />
                        <span>Domain: <code>@{college.domain || 'N/A'}</code></span>
                      </div>
                    </div>

                    <div className="college-card-footer" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => navigate(`/colleges/${college._id}`)}
                      >
                        Manage Institution <FiArrowRight />
                      </button>

                      {isAdmin && (
                        <button
                          className="btn btn-sm btn-ghost"
                          style={{ color: 'var(--danger)' }}
                          onClick={() => setDeletingCollege(college)}
                          title="Delete College"
                        >
                          <FiTrash2 /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(deletingCollege)}
        title="Delete Institution?"
        message={`Are you sure you want to delete "${deletingCollege?.collegeName}"? This action cannot be undone.`}
        confirmText="Delete College"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeletingCollege(null)}
      />
    </div>
  );
};

export default Colleges;
